/**
 * Miata Registry
 * Copyright (C) 2024-2026 Matthew Congrove
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import {
	TRarityLevel,
	TStoredVinDetails,
	TVinDecodeStatus,
} from '../types/Car';
import { toTitleCase } from './common';
import { countryNameToCode } from './location';

export const RARITY_LEVELS: TRarityLevel[] = [
	'historically-significant',
	'exceptionally-rare',
	'very-rare',
	'rare',
	'limited-edition',
];

export const formatRarityLevel = (level: string): string =>
	level
		.split('-')
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');

export const hasSequence = (
	sequence?: number | string | null
): sequence is number => {
	if (sequence == null || sequence === '') return false;

	const value = Number(sequence);

	return Number.isFinite(value) && value > 0;
};

export const parseSequence = (
	value: FormDataEntryValue | string | number | null | undefined
): number | null => {
	if (value == null || value === '') return null;

	const sequence = Number(value);

	return Number.isFinite(sequence) && sequence > 0 ? sequence : null;
};

/** ISO 3779 VIN charset — I, O, Q omitted to avoid 1/0 confusion. */
const VIN_CHAR = '[A-HJ-NPR-Z0-9]';

export const VIN_INPUT_PATTERN = `(?:[A-Z0-9]{5}-[A-Z0-9]{6}|JM${VIN_CHAR}{15})`;

export const VIN_VALIDATION_MESSAGE =
	'Enter a 17-character VIN starting with JM (no I, O, or Q), or a chassis number as XXXXX-XXXXXX.';

const VIN_PATTERN = new RegExp(`^${VIN_INPUT_PATTERN}$`, 'i');

/** Strip whitespace and uppercase so pasted VINs like "JM1 NDAC …" stay valid. */
export const normalizeVinInput = (value: string): string =>
	value.replace(/\s+/g, '').toUpperCase();

export const isValidVin = (vin: string | null | undefined): boolean => {
	if (!vin) return false;

	const normalized = normalizeVinInput(vin);

	return normalized.length > 0 && VIN_PATTERN.test(normalized);
};

const CHASSIS_PATTERN = /^[A-Z0-9]{5}-[A-Z0-9]{6}$/i;
const FULL_VIN_PATTERN = new RegExp(`^JM${VIN_CHAR}{15}$`, 'i');

export const isChassisNumber = (vin: string | null | undefined): boolean => {
	if (!vin) return false;

	return CHASSIS_PATTERN.test(normalizeVinInput(vin));
};

export const isFullVin = (vin: string | null | undefined): boolean => {
	if (!vin) return false;

	return FULL_VIN_PATTERN.test(normalizeVinInput(vin));
};

export const parseEditionYear = (
	editionName: string | null | undefined
): number | null => {
	if (!editionName) return null;

	const match = editionName.trim().match(/^(\d{4})/);

	return match ? Number(match[1]) : null;
};

export type TVinDetails = {
	BodyClass?: string;
	DisplacementL?: string;
	EngineCylinders?: string;
	EngineHP?: string;
	EngineHP_to?: string;
	EngineModel?: string;
	ErrorCode?: string;
	FuelTypePrimary?: string;
	Manufacturer?: string;
	Model?: string;
	ModelYear?: string;
	PlantCity?: string;
	PlantCountry?: string;
	PlantState?: string;
	VehicleDescriptor?: string;
	VIN?: string;
};

export type TVinDecodeFields = {
	manufacture_city: string | null;
	manufacture_prefecture: string | null;
	vin_decode_status: TVinDecodeStatus;
	vin_details: TStoredVinDetails | null;
};

const emptyVinDecodeFields = (status: TVinDecodeStatus): TVinDecodeFields => ({
	manufacture_city: null,
	manufacture_prefecture: null,
	vin_decode_status: status,
	vin_details: null,
});

const toStoredVinDetails = (details: TVinDetails): TStoredVinDetails => {
	const pick = (value?: string) => {
		if (!value || value === 'Not Applicable') return undefined;

		return value;
	};

	return {
		bodyClass: pick(details.BodyClass),
		displacementL: pick(details.DisplacementL),
		engineCylinders: pick(details.EngineCylinders),
		engineHP: pick(details.EngineHP),
		engineHP_to: pick(details.EngineHP_to),
		engineModel: pick(details.EngineModel),
		fuelTypePrimary: pick(details.FuelTypePrimary),
		model: pick(details.Model),
		modelYear: pick(details.ModelYear),
		vehicleDescriptor: pick(details.VehicleDescriptor),
	};
};

export const getVinDetails = async (
	vin: string,
	year: number
): Promise<TVinDetails | null> => {
	try {
		if (!vin || !year) return null;

		const response = await fetch(
			`https://vpic.nhtsa.dot.gov/api/vehicles/DecodeVinValues/${encodeURIComponent(vin)}?format=json&modelyear=${year}`
		);

		if (!response.ok) return null;

		const data = await response.json();

		if (data.Results?.[0]) {
			return data.Results[0];
		}

		return null;
	} catch (error) {
		console.error('Error fetching VIN details:', error);

		return null;
	}
};

export const parseVinApiErrorCodes = (
	details: TVinDetails | null
): string[] => {
	if (!details?.ErrorCode) return [];

	// NHTSA joins codes with "," (sometimes ";")
	return details.ErrorCode.split(/[;,]/)
		.map((code) => code.trim())
		.filter(Boolean);
};

/** VIN decoded cleanly. Code 12 (year mismatch) is allowed here — check separately. */
export const isVinApiValid = (details: TVinDetails | null): boolean => {
	const codes = parseVinApiErrorCodes(details);

	if (!codes.includes('0')) return false;

	return codes.every((code) => code === '0' || code === '12');
};

/** Edition year ≠ VIN model year (NHTSA code 12 and/or ModelYear field). */
export const hasVinModelYearMismatch = (
	details: TVinDetails | null,
	editionYear: number | null | undefined
): boolean => {
	if (!details || editionYear == null) return false;

	if (parseVinApiErrorCodes(details).includes('12')) return true;

	const modelYear = details.ModelYear ? Number(details.ModelYear) : NaN;

	return Number.isFinite(modelYear) && modelYear !== editionYear;
};

export const buildVinDecodeFields = async (
	vin: string | null | undefined,
	year: number | null | undefined
): Promise<TVinDecodeFields | null> => {
	if (!vin?.trim()) return null;

	if (!isFullVin(vin)) {
		return emptyVinDecodeFields('skipped');
	}

	if (!year) {
		return emptyVinDecodeFields('error');
	}

	const details = await getVinDetails(vin.trim(), year);

	if (!details) {
		return emptyVinDecodeFields('error');
	}

	if (!isVinApiValid(details)) {
		return emptyVinDecodeFields('invalid');
	}

	const city = details.PlantCity ? toTitleCase(details.PlantCity) : null;
	const prefecture = details.PlantState
		? toTitleCase(details.PlantState)
		: null;

	return {
		manufacture_city: city,
		manufacture_prefecture: prefecture,
		vin_decode_status: 'ok',
		vin_details: toStoredVinDetails(details),
	};
};

export type TMileageUnit = 'mi' | 'km';

const KM_PER_MILE = 1.609344;

export const parseMileageInput = (
	value: FormDataEntryValue | string | number | null | undefined,
	unit: TMileageUnit = 'mi'
): number | null => {
	if (value == null || value === '') return null;

	const raw = Number(String(value).replace(/[^0-9]/g, ''));

	if (!Number.isFinite(raw) || raw < 0) return null;

	return unit === 'km' ? Math.round(raw / KM_PER_MILE) : raw;
};

export const convertMileageDisplay = (
	value: number,
	from: TMileageUnit,
	to: TMileageUnit
): number => {
	if (!Number.isFinite(value) || from === to) return value;

	return from === 'mi'
		? Math.round(value * KM_PER_MILE)
		: Math.round(value / KM_PER_MILE);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const formatEngineDetails = (details: any) => {
	const displacement = details.DisplacementL
		? `${Number(details.DisplacementL).toFixed(1)}L`
		: '';

	const cylinders = details.EngineCylinders
		? `${details.EngineCylinders}-cyl`
		: '';

	const configuration =
		details.EngineConfiguration?.toLowerCase() === 'inline'
			? 'Inline'
			: details.EngineConfiguration;

	const horsepower = details.EngineHP
		? details.EngineHP_to
			? `${details.EngineHP}–${details.EngineHP_to}hp`
			: `${details.EngineHP}hp`
		: '';

	return (
		[displacement, configuration, cylinders, horsepower]
			.filter(Boolean)
			.join(' ')
			.trim() || 'Not specified'
	);
};

export const formatPlantLocation = (details: TVinDetails | null) => {
	if (!details) return '';

	// Default to Japan for Mazda VINs starting with JM1
	const isJapaneseMazda = details?.VIN?.startsWith('JM1');

	const city = details?.PlantCity ? toTitleCase(details?.PlantCity) : '';
	const state = details?.PlantState ? toTitleCase(details?.PlantState) : '';
	const country = details?.PlantCountry
		? countryNameToCode(details?.PlantCountry)
		: isJapaneseMazda
			? 'JP'
			: '';

	return [city, state, country].filter(Boolean).join(', ');
};

export const formatManufactureLocation = (
	city?: string | null,
	prefecture?: string | null
) => {
	return [city, prefecture, 'JP'].filter(Boolean).join(', ');
};

export const formatEditionColor = (color: string): string =>
	color.toLowerCase() === 'various' ? 'Various Colors' : color;

export const colorMap: Record<string, string> = {
	'aero grey metallic': '#8A8D90',
	'aluminium silver metallic': '#C5C9CD',
	'aurora blue mica': '#0A1371',
	'black mica': '#000000',
	'blazing yellow mica': '#FFD700',
	'brave blue mica': '#1B4D8D',
	'brilliant black': '#000000',
	'british racing green': '#2C4C3B',
	'chaste white': '#FAFAF7',
	'classic red': '#E2231A',
	'classic white': '#F4F4F0',
	'copper red mica': '#8E3B36',
	'crystal blue metallic': '#74B1D7',
	'crystal white pearlescent': '#F0ECE4',
	'dark blue': '#00008B',
	'dolphin grey mica': '#7B8086',
	'emerald green mica': '#024930',
	'evolution orange mica': '#F0A500',
	'excellent green mica': '#0A5C40',
	'icy blue metallic': '#A5F2F3',
	'jet black mica': '#0E0E10',
	'laguna blue': '#2A4BA0',
	'lava orange mica': '#E84828',
	'light green': '#9BC47A',
	'lightning yellow': '#FAE875',
	'liquid silver metallic': '#B8BEC4',
	'machine grey metallic': '#5A5A5E',
	'mahogany mica': '#4B273D',
	'marina green mica': '#1B4D3E',
	'medium blue': '#3A6BA5',
	'merlot mica': '#641F34',
	'metallic yellow': '#E6C84A',
	'meteor grey mica': '#5C6168',
	'metropolitan grey mica': '#6A6E74',
	'midnight blue mica': '#2A1B57',
	'montego blue mica': '#1B4B6D',
	'pure white': '#FFFFFF',
	'racing orange': '#F5550B',
	'radiant ebony mica': '#1C1218',
	raspberry: '#C42B4A',
	'renown livery': '#F15408',
	'sapphire blue mica': '#002B7F',
	'snowflake white pearl mica': '#F0F0F0',
	'soul red metallic': '#A71D31',
	'sparkle green metallic': '#3D8B5C',
	'sparkling black mica': '#141416',
	'spirited green metallic': '#6AB438',
	'starlight blue mica': '#1B365D',
	'strato blue mica': '#002B7F',
	'sunburst yellow': '#FFD700',
	'sunlight silver metallic': '#C4C7C1',
	'teal green': '#2A7A6E',
	'titanium grey metallic': '#6D6F64',
	'true red': '#D90B13',
	'twilight blue mica': '#1B365D',
	various: '#CCCCCC',
	'velocity red mica': '#C60505',
	'vivid orange': '#FF5600',
};
